/**
 * Performance Monitoring Utilities
 * Tracks Core Web Vitals and custom metrics
 */

// Track page load performance
export const measurePageLoad = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('load', () => {
    const perfData = window.performance.timing;
    const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
    const connectTime = perfData.responseEnd - perfData.requestStart;
    const renderTime = perfData.domComplete - perfData.domLoading;

    console.log('⚡ Performance Metrics:');
    console.log(`  Page Load Time: ${pageLoadTime}ms`);
    console.log(`  Server Connection: ${connectTime}ms`);
    console.log(`  Render Time: ${renderTime}ms`);

    // Send to analytics if available
    if (window.gtag) {
      window.gtag('event', 'timing_complete', {
        name: 'page_load',
        value: pageLoadTime,
        event_category: 'Performance'
      });
    }
  });
};

// Core Web Vitals - Largest Contentful Paint (LCP)
export const measureLCP = () => {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      console.log('🎨 LCP (Largest Contentful Paint):', lastEntry.renderTime || lastEntry.loadTime);
      
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          name: 'LCP',
          value: Math.round(lastEntry.renderTime || lastEntry.loadTime),
          event_category: 'Web Vitals'
        });
      }
    });

    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  } catch (e) {
    console.error('LCP measurement failed:', e);
  }
};

// First Input Delay (FID)
export const measureFID = () => {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        console.log('⚡ FID (First Input Delay):', entry.processingStart - entry.startTime);
        
        if (window.gtag) {
          window.gtag('event', 'web_vitals', {
            name: 'FID',
            value: Math.round(entry.processingStart - entry.startTime),
            event_category: 'Web Vitals'
          });
        }
      });
    });

    observer.observe({ entryTypes: ['first-input'] });
  } catch (e) {
    console.error('FID measurement failed:', e);
  }
};

// Cumulative Layout Shift (CLS)
export const measureCLS = () => {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  try {
    let clsScore = 0;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          clsScore += entry.value;
        }
      }
      
      console.log('📐 CLS (Cumulative Layout Shift):', clsScore);
      
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          name: 'CLS',
          value: Math.round(clsScore * 1000),
          event_category: 'Web Vitals'
        });
      }
    });

    observer.observe({ entryTypes: ['layout-shift'] });
  } catch (e) {
    console.error('CLS measurement failed:', e);
  }
};

// Initialize all performance measurements
export const initPerformanceMonitoring = () => {
  if (typeof window === 'undefined') return;
  
  measurePageLoad();
  measureLCP();
  measureFID();
  measureCLS();
};

// Custom metric: Track component render time
export const measureComponentRender = (componentName, callback) => {
  const startTime = performance.now();
  callback();
  const endTime = performance.now();
  const renderTime = endTime - startTime;
  
  console.log(`🔧 ${componentName} render time: ${renderTime.toFixed(2)}ms`);
  
  return renderTime;
};

// Check if user has slow connection
export const isSlowConnection = () => {
  if (typeof navigator === 'undefined' || !navigator.connection) return false;
  
  const connection = navigator.connection;
  return (
    connection.effectiveType === 'slow-2g' ||
    connection.effectiveType === '2g' ||
    connection.saveData === true
  );
};

export default {
  initPerformanceMonitoring,
  measurePageLoad,
  measureLCP,
  measureFID,
  measureCLS,
  measureComponentRender,
  isSlowConnection
};
